// ...existing code...
import React from "react";
import { motion } from "framer-motion";
import {
  FaStethoscope,
  FaCloud,
  FaHeartbeat,
  FaShieldAlt,
  FaUserMd,
  FaRegClock,
  FaNotesMedical,
  FaClipboard,
  FaDatabase,
  FaServer,
} from "react-icons/fa";
import "./Healthcare";
import { Link } from "react-router-dom";

const FEATURES = [
  {
    icon: <FaClipboard />,
    title: "Client-Centric Delivery ",
    text: "Lyros follows a customer-first philosophy, aligning every process with client objectives to ensure seamless collaboration and measurable outcomes. ",
  },
  {
    icon: <FaDatabase />,
    title: "Process Excellence ",
    text: "Our BPO framework is built on continuous improvement, automation, and data accuracy, ensuring high-quality and consistent service delivery. ",
  },
  {
    icon: <FaServer />,
    title: "Technology Integration ",
    text: "By incorporating AI, RPA, and cloud-based tools, Lyros enhances operational efficiency and minimizes turnaround time across all service lines. ",
  },
  {
    icon: <FaCloud />,
    title: "Data Security & Compliance",
    text: "We adhere to global data protection standards and industry regulations, ensuring every transaction and interaction remains secure and confidential.",
  },
  {
    icon: <FaShieldAlt />,
    title: "Scalable Solutions ",
    text: "Lyros provides flexible and scalable outsourcing models tailored to meet the evolving needs of businesses of all sizes and industries.",
  },
  {
    icon: <FaRegClock />,
    title: "Performance Transparency ",
    text: "Through detailed reporting and analytics, we maintain complete transparency, enabling clients to track performance and outcomes in real time.",
  },
];

const WHY = [
  {
    icon: <FaShieldAlt />,
    title: "Client-Focused Solutions ",
    text: "We prioritize your business goals, ensuring our services are aligned to deliver measurable outcomes and exceptional value. ",
  },
  {
    icon: <FaUserMd />,
    title: "Process Excellence ",
    text: "Our operations are built on efficiency, accuracy, and continuous improvement, guaranteeing consistent and high-quality service delivery. ",
  },
  {
    icon: <FaCloud />,
    title: "Advanced Technology ",
    text: "We leverage AI, RPA, and cloud-based tools to streamline workflows, reduce turnaround time, and enhance overall productivity. ",
  },
];

const Card = ({ icon, title, text, i }) => (
  <motion.article
    className="hc-card"
    initial={{ opacity: 0, y: 18, scale: 0.995 }}
    whileInView={{ opacity: 1, y: 0, scale: 1 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6, delay: i * 0.08 }}
    aria-labelledby={`hc-card-${i}-title`}
  >
    <div className="hc-icon" aria-hidden>
      {icon}
    </div>
    <div className="hc-card-body">
      <h3 id={`hc-card-${i}-title`}>{title}</h3>
      <p>{text}</p>
    </div>
  </motion.article>
);

const BusinessProcessOutsourcing = () => {
  return (
    <main className="digital-container">
      {/* Header without external image - decorative gradient + shapes via CSS */}
    
           <header className="digital-header">
             <div className="header-content">
               <h1>BusinessProcessOutsourcing</h1>
               <p className="subtitle">
              Providing reliable outsourcing services that streamline operations, enhance customer experience, and reduce costs through expert support and process optimization.  </p>
     
               <div className="header-ctas">
                  <Link
               to="/contact" // ✅ Navigate to Contact page
               className="btn-primary"
               aria-label="Contact Lyros"
             >
               Talk to our Expert team
             </Link>
               </div>
             </div>
     
             <svg className="header-decor" viewBox="0 0 800 200" preserveAspectRatio="none" aria-hidden="true">
               <defs>
                 <linearGradient id="gB" x1="0" x2="1">
                   <stop offset="0" stopColor="#0ea5e9" stopOpacity="0.14" />
                   <stop offset="1" stopColor="#6366f1" stopOpacity="0.06" />
                 </linearGradient>
               </defs>
               <path d="M0 140 C160 40 360 240 800 80 L800 200 L0 200 Z" fill="url(#gB)"></path>
             </svg>
           </header>
     
      <section className="container hc-intro">
        <motion.p
          className="section-desc"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
         Lyros is a strategic BPO partner helping organizations achieve operational excellence and sustainable growth. We handle non-core functions like customer support, finance, HR, and technical assistance — enabling businesses to focus on their core goals. Through advanced technology, data insights, and skilled professionals, we ensures efficiency, accuracy, and scalability, empowering enterprises to boost productivity, reduce costs, and stay competitive in a dynamic market. </motion.p>
      </section>

      <section className="container hc-features">
        <h2 className="section-title">Our Approach</h2>
        <div className="hc-cards">
          {FEATURES.map((f, idx) => (
            <Card key={idx} icon={f.icon} title={f.title} text={f.text} i={idx} />
          ))}
        </div>
      </section>

      <section className="container hc-examples">
        <h2 className="section-title"> Our Business Process Outsourcing Services</h2>
        <div className="hc-examples-grid">
          <motion.div
            className="example-card"
            initial={{ opacity: 0, x: -12 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h4>Customer Support Services </h4>
            <p>Lyros delivers 24/7 multi-channel support, including voice, email, and chat, ensuring exceptional customer experiences and satisfaction. </p>
          </motion.div>

          <motion.div
            className="example-card"
            initial={{ opacity: 0, x: 12 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.08 }}
          >
            <h4>Back-Office Operations </h4>
            <p>Our team delivers precise and efficient record management, processing, and reconciliation services while ensuring complete confidentiality. </p>
          </motion.div>

          <motion.div
            className="example-card"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.16 }}
          >
            <h4>Technical Support </h4>
            <p>We provide expert IT assistance, troubleshooting, and system management to keep your operations running smoothly. </p>
          </motion.div>
        </div>
      </section>

      <section className="container hc-why">
        <h2 className="section-title">Why Choose Lyros</h2>
        <div className="hc-why-grid">
          {WHY.map((w, i) => (
            <motion.div
              className="why-card"
              key={i}
              initial={{ opacity: 0, scale: 0.98 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
            >
              <div className="why-icon" aria-hidden>
                {w.icon}
              </div>
              <div className="why-content">
                <h4>{w.title}</h4>
                <p>{w.text}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>
    </main>
  );
};

export default BusinessProcessOutsourcing;
// ...existing code...